import { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';
import * as XLSX from 'xlsx';

import { alertService } from '../../../../shared/services/alertService';
import {
  useBreadcrumb,
  BreadcrumbItem,
} from '../../../../layouts/BreadcrumbContext';
import {
  useLazyEditCounsellingQuery,
  useGetPanelQuery,
  useGetStaffQuery,
  useLazyGetSubjectQuery,
  useLazyGetSeniorityOrderQuery,
  useBulkUploadSeniorityMutation,
  usePostPlanCounsellingMutation,
} from '../../counselling.services';
import {
  subjectListKey,
  Option,
} from '../../../../shared/services/commonService';

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Migrated from Angular `SeniorityOrderComponent`
 * (src/app/views/pages/counselling/seniority-order).
 *
 * Computes and displays the seniority-ordered candidate list for a counselling,
 * lets the user review it (column chooser + global/per-column search), export it
 * to Excel, and commit the ordering (bulk-write each teacher's seniority number,
 * then flag the counselling as reset).
 *
 * Reached from the Seniority Upload Type hub, which passes the counselling id via
 * router navigation state (`{ path }`). The old manual condition-builder UI in
 * Angular was fully commented out, so only the results-table path is ported.
 */

const str = (v: any): string =>
  v === null || v === undefined ? '' : String(v);

/** Filters handed off from the upstream screen via router navigation state. */
interface SeniorityOrderNavState {
  path?: unknown;
  subject?: unknown;
  panelType?: unknown;
  block?: unknown;
  manageId?: unknown;
  canUpdtBy: unknown;
}

/** Angular used moment(...).format('DD/MM/yyyy'); keep the same output. */
const formatDate = (value: any): string => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

interface ColumnDef {
  field: string;
  header: string;
}

// Angular selectedCounClmn (order preserved).
const ALL_COLUMNS: ColumnDef[] = [
  { field: 'seniority_state', header: 'Old Seniority' },
  { field: 'seniority_state_new', header: 'New Order Seniority' },
  { field: 'teacher_id', header: 'Teacher Id' },
  { field: 'name', header: 'Name' },
  { field: 'desgnationLabel', header: 'Designation' },
  { field: 'subjectNme', header: 'Subject' },
  { field: 'udise_code', header: 'Udise Code' },
  { field: 'school_name', header: 'School Name' },
  { field: 'DistrictNme', header: 'District' },
  { field: 'EduDisNme', header: 'Education District' },
  { field: 'blockNme', header: 'Block' },
  { field: 'panelNme', header: 'Panel' },
  { field: 'special_priority', header: 'Special Priority' },
  { field: 'priority', header: 'Priority' },
  { field: 'DojSch', header: 'Date of joining  - Present Station' },
  { field: 'DojPost', header: 'Date of joining - Present Post' },
  { field: 'DateApp', header: 'Date of Appointment' },
  { field: 'dojBlk', header: 'Date of joining - Block' },
  { field: 'Dob', header: 'Date of birth' },
];

const GLOBAL_FILTER_FIELDS = ALL_COLUMNS.map((c) => c.field);

const SeniorityOrder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSubItems, clearSubItems } = useBreadcrumb();

  // Hand-off from the hub comes via router navigation state (clean URL).
  const navState = (location.state ?? {}) as SeniorityOrderNavState;
  const counId = useMemo(() => str(navState.path), [navState.path]);

  // Filters handed off via navigation state (Angular read these from
  // localStorage: subject / PanelType / block / ManageId).
  const localSubject = useMemo(() => str(navState.subject), [navState.subject]);
  const localPanel = useMemo(
    () => str(navState.panelType),
    [navState.panelType]
  );
  const localBlock = useMemo(() => str(navState.block), [navState.block]);
  const localMange = useMemo(() => str(navState.manageId), [navState.manageId]);

  const { data: panelRaw } = useGetPanelQuery();
  const { data: staffRaw } = useGetStaffQuery();
  const [triggerEditCounselling] = useLazyEditCounsellingQuery();
  const [triggerGetSubject] = useLazyGetSubjectQuery();
  const [triggerSeniorityOrder] = useLazyGetSeniorityOrderQuery();
  const [bulkUploadSeniority] = useBulkUploadSeniorityMutation();
  const [postPlanCounselling] = usePostPlanCounsellingMutation();

  const [counsellingDetails, setCounsellingDetails] = useState<any>(null);
  const [seniorityCandidateList, setSeniorityCandidateList] = useState<any[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [excelLoading, setExcelLoading] = useState(false);

  const [selectedCols, setSelectedCols] = useState<ColumnDef[]>(ALL_COLUMNS);
  const orderedCols = useMemo(
    () => ALL_COLUMNS.filter((col) => selectedCols.includes(col)),
    [selectedCols]
  );
  const [globalFilter, setGlobalFilter] = useState('');

  // Angular: getStaff() -> AllStaffType.
  const designationList: Option[] = useMemo(
    () => staffRaw?.AllStaffType ?? [],
    [staffRaw]
  );
  // Angular: getPanel() -> Panel.
  const panelList: { id: string; label: string }[] = useMemo(
    () => panelRaw?.Panel ?? [],
    [panelRaw]
  );

  // ── Core load + ordering (Angular getOneCounselling + handleNext) ───────────
  const loadSeniority = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await triggerEditCounselling({
        counsellingId: counId,
      }).unwrap();

      if (!(res?.dataStatus && (res.result ?? []).length > 0)) {
        alertService.error('Something went wrong');
        setLoading(false);
        return;
      }

      const details = res.result[0];
      setCounsellingDetails(details);

      // Subject list keyed by designation (Angular getSubject switch).
      let subjectList: Option[] = [];
      try {
        const subjectData: any = await triggerGetSubject().unwrap();
        const key = subjectListKey(str(details.Designation));
        const list = subjectData?.[key];
        subjectList = Array.isArray(list)
          ? list.map((x: any) => ({ label: x.label, value: str(x.value) }))
          : [];
      } catch {
        subjectList = [];
      }

      // Resolve panel + subject labels from the localStorage filters.
      const panelMatch = panelList.find((p) => str(p.id) === str(localPanel));
      const panelLabel = panelMatch?.label ?? '';
      const subjectMatch = subjectList.find(
        (s) => str(s.value) === str(localSubject)
      );
      const subjectLabel = subjectMatch?.label ?? '';

      // Angular handleNext guard: needs a SeniorityOrder that isn't '0'.
      if (!details?.SeniorityOrder || str(details.SeniorityOrder) === '0') {
        setSeniorityCandidateList([]);
        setLoading(false);
        alertService.error('Please order seniority condition');
        return;
      }

      const records = {
        CnsId: counId,
        Loc: details?.Location,
        Sub: str(details?.Designation) === '103' ? '' : localSubject,
        PanelType: localPanel,
        BlkId: localBlock,
        MngId: localMange,
        Order: details?.SeniorityOrder,
      };

      const orderRes: any = await triggerSeniorityOrder(records).unwrap();
      if (!(orderRes?.dataStatus && (orderRes.result ?? []).length > 0)) {
        setSeniorityCandidateList([]);
        setLoading(false);
        return;
      }

      // Keep only approved rows, then labelize + format (Angular loop).
      const rows = (orderRes.result as any[])
        .filter((ftr) => ftr.HmAprvSts === 'Approved')
        .map((row, i) => {
          const desMatch = designationList.find(
            (d) => str(d.value) === str(row.desgnation)
          );
          const subMatch = subjectList.find(
            (s) => str(s.value) === str(row.subject)
          );
          const subjectNme =
            subjectLabel && str(details?.Designation) !== '103'
              ? subjectLabel
              : (subMatch?.label ?? '');

          return {
            ...row,
            DojSch: formatDate(row.DojSch),
            DojPost: formatDate(row.DojPost),
            DateApp: formatDate(row.DateApp),
            dojBlk: formatDate(row.dojBlk),
            Dob: formatDate(row.Dob),
            seniority_state_new: i + 1,
            desgnationLabel: desMatch?.label ?? '',
            subjectNme,
            panelNme: panelLabel || '',
            special_priority:
              str(row.special_priority) === '1' ? 'ஆம்' : 'இல்லை',
          };
        });

      setSeniorityCandidateList(rows);
      setLoading(false);
    } catch {
      setLoading(false);
      alertService.error('Something went wrong');
    }
  }, [
    counId,
    localSubject,
    localPanel,
    localBlock,
    localMange,
    panelList,
    designationList,
    triggerEditCounselling,
    triggerGetSubject,
    triggerSeniorityOrder,
  ]);

  // Wait for the lookup lists before ordering (labels depend on them).
  useEffect(() => {
    if (!panelRaw || !staffRaw) return;
    loadSeniority();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelRaw, staffRaw, counId]);

  // ── Commit ordering (Angular seniorityReset) ────────────────────────────────
  const seniorityReset = useCallback(async () => {
    try {
      const res: any = await postPlanCounselling({
        records: {
          IndexId: counsellingDetails?.IndexId,
          SeniorityReset: '1',
        },
      }).unwrap();
      if (res?.dataStatus) {
        alertService.success('Seniority ordered successfully');
        loadSeniority();
      }
    } catch {
      alertService.error('Something went wrong');
    }
  }, [counsellingDetails, postPlanCounselling, loadSeniority]);

  // ── Submit: bulk write seniority in chunks of 1000 (Angular handleBulkSubmit)
  const handleBulkSubmit = useCallback(async () => {
    const ary = seniorityCandidateList.map((row, i) => ({
      TchrId: row.teacher_id,
      CnsId: counId,
      IndexId: row.uptId,
      Seniority: i + 1,
      isactive: 1,
    }));

    const splitSize = 1000;
    const chunks: any[][] = [];
    for (let i = 0; i < ary.length; i += splitSize) {
      chunks.push(ary.slice(i, i + splitSize));
    }

    try {
      let allOk = true;
      for (const chunk of chunks) {
        const res: any = await bulkUploadSeniority({
          records: chunk,
        }).unwrap();
        if (!res?.dataStatus) {
          allOk = false;
          alertService.error(res?.message ?? 'Something went wrong');
          break;
        }
      }
      if (allOk && chunks.length > 0) {
        seniorityReset();
      }
    } catch {
      alertService.error('Something went wrong');
    }
  }, [seniorityCandidateList, counId, bulkUploadSeniority, seniorityReset]);

  // ── Excel export (Angular exportExcel) ──────────────────────────────────────
  const exportExcel = useCallback(() => {
    setExcelLoading(true);
    const excelArray = seniorityCandidateList.map((row) => ({
      Seniority: row.seniority_state,
      TeacherId: row.teacher_id,
      Name: row.name,
      Gender: row.gender,
      'Date of birth': row.Dob,
      Designation: row.desgnationLabel,
      Subject: row.subjectNme,
      'Udise Code': row.udise_code,
      'School Name': row.school_name,
      District: row.DistrictNme,
      'Education District': row.EduDisNme,
      Block: row.blockNme,
      Panel: row.panelNme,
      Priority: row.priority,
      'Date of joining - Present Station': row.DojSch,
      'Date of joining - Present Post': row.DojPost,
      'Date of Appointment': row.DateApp,
      'Date of joining - Block': row.dojBlk,
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelArray);
    const workbook = {
      Sheets: { 'Seniority Order': worksheet },
      SheetNames: ['Seniority Order'],
    };
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Seniority Order_export_${new Date().getTime()}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
    setExcelLoading(false);
  }, [seniorityCandidateList]);

  // ── Navigation ──────────────────────────────────────────────────────────────
  const back = useCallback(() => navigate(-1), [navigate]);
  const handleCounsellingURL = useCallback(
    () => navigate('/counselling/seniority'),
    [navigate]
  );
  // Angular: /counselling/seniority-type?path=CounID (the upload-type hub).
  const handleSeniorityType = useCallback(() => {
    navigate('/counselling/seniority-type', {
      state: {
        path: counId,
        subject: localSubject,
        panelType: localPanel,
        block: localBlock,
        manageId: localMange,
      },
    });
  }, [navigate, counId, localSubject, localPanel, localBlock, localMange]);

  // Feed the app-shell breadcrumb (Breadcrumb.tsx renders Home + these):
  //   Seniority Update / <Title> / Seniority Order
  useEffect(() => {
    const items: BreadcrumbItem[] = [
      { label: 'Seniority Update', onClick: handleCounsellingURL },
    ];
    if (counsellingDetails?.Title) {
      items.push({
        label: counsellingDetails.Title,
        onClick: handleSeniorityType,
      });
    }
    items.push({ label: 'Seniority Order' });
    setSubItems(items);
    return () => clearSubItems();
  }, [
    counsellingDetails,
    handleCounsellingURL,
    handleSeniorityType,
    setSubItems,
    clearSubItems,
  ]);

  const hasRows = seniorityCandidateList.length > 0;

  return (
    <div className="main-body-content">
      {/* Breadcrumb handled by the app shell (see the useBreadcrumb effect). */}
      <div className="card">
        <div className="Counselling-Secondary">
          <DataTable
            value={seniorityCandidateList}
            loading={loading}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 25, 50]}
            rowHover
            responsiveLayout="scroll"
            emptyMessage="No Records Found"
            globalFilter={globalFilter}
            globalFilterFields={GLOBAL_FILTER_FIELDS}
            showGridlines
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
            header={
              <div className="row">
                <div className="col-md-3 col-12 mb-2">
                  <MultiSelect
                    value={selectedCols}
                    options={ALL_COLUMNS}
                    optionLabel="header"
                    placeholder="Choose Columns"
                    selectedItemsLabel="{0} columns selected"
                    className="w-100"
                    onChange={(e) => setSelectedCols(e.value)}
                  />
                </div>
                <div className="col-md-4 col-12"></div>
                <div className="col-md-2 col-12 text-end">
                  <Button
                    label="Export as Excel"
                    icon={excelLoading ? 'pi pi-spin pi-spinner' : ''}
                    className="p-button-success mt-2"
                    disabled={!hasRows}
                    onClick={exportExcel}
                  />
                </div>
                <div className="col-md-3 col-12">
                  <InputText
                    type="text"
                    placeholder="Search.."
                    className="w-100"
                    value={globalFilter}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                  />
                </div>
              </div>
            }
          >
            {orderedCols.map((col) => (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                sortable
                // Size each column to its header label: keep the header on one
                // line and give it a min-width derived from the label length
                // (plus room for the sort icon), so longer headers get wider
                // columns instead of wrapping/truncating.
                headerStyle={{ whiteSpace: 'nowrap' }}
                style={{ minWidth: `${col.header.length * 0.4 + 2}rem` }}
              />
            ))}
          </DataTable>

          <div className="row m-3">
            <div className="col-12 text-end">
              <Button
                label="Submit"
                className="p-button-success"
                onClick={handleBulkSubmit}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeniorityOrder;

/* eslint-enable @typescript-eslint/no-explicit-any */
